import { type QueryClient, queryOptions, useMutation } from '@tanstack/react-query'
import type {
  AddRtaAlert,
  CoverageRuleInput,
  RtaAlertOverridesPatch,
  RtaAlertStatus,
  RtaSettings,
  RtaStatus,
  RtaTopicInput,
  TemplateAssetContentType,
} from '@headline/schemas'
import { api } from './api'
import { fail } from './query-fail'

/**
 * Real-time alerts (CLI-744) — queries and mutations for the RTA pages.
 *
 * Keys nest under `['rta', rtaId]` so one invalidation after any triage action refreshes the
 * feed, its counts and the audit trail together. The feed's filters are low-cardinality (a
 * status, a topic, a sort, a view) and go in the key; the search box is debounced by the caller
 * before it reaches one, per the query-key rule.
 */

/** Alerts land every few minutes; the feed and the chips re-ask on this beat while open. */
const RTA_POLL_MS = 60_000

export function rtasQuery(scope: { accountId?: string; subaccountId?: string }) {
  return queryOptions({
    queryKey: ['rtas', scope.accountId ?? null, scope.subaccountId ?? null],
    queryFn: async () => {
      const res = await api.api.rtas.$get({ query: scope })
      if (!res.ok) return fail(res, 'Failed to load RTAs')
      return res.json()
    },
    refetchInterval: RTA_POLL_MS,
  })
}

/** Pending counts for every RTA — one small grouped read the session tabs share. */
export const rtaCountsQuery = queryOptions({
  queryKey: ['rta-counts'],
  queryFn: async () => {
    const res = await api.api.rtas.counts.$get()
    if (!res.ok) return fail(res, 'Failed to load RTA counts')
    return res.json()
  },
  refetchInterval: RTA_POLL_MS,
  // A chip is a nicety: never retry a 403 (someone without rtas:curate) into a storm.
  retry: false,
})

export function rtaQuery(rtaId: string) {
  return queryOptions({
    queryKey: ['rta', rtaId],
    queryFn: async () => {
      const res = await api.api.rtas[':id'].$get({ param: { id: rtaId } })
      if (res.status === 404) throw new Error('This RTA no longer exists.')
      if (!res.ok) return fail(res, 'Failed to load the RTA')
      return res.json()
    },
  })
}

export interface RtaFeedFilters {
  view: 'active' | 'deleted'
  status?: RtaAlertStatus | undefined
  topicId?: string | undefined
  q?: string | undefined
  sort: 'newest' | 'pending'
}

export function rtaAlertsQuery(rtaId: string, filters: RtaFeedFilters) {
  return queryOptions({
    queryKey: ['rta', rtaId, 'alerts', filters],
    queryFn: async () => {
      const res = await api.api.rtas[':id'].alerts.$get({
        param: { id: rtaId },
        query: {
          view: filters.view,
          sort: filters.sort,
          ...(filters.status && { status: filters.status }),
          ...(filters.topicId && { topicId: filters.topicId }),
          ...(filters.q && { q: filters.q }),
        },
      })
      if (!res.ok) return fail(res, 'Failed to load alerts')
      return res.json()
    },
    refetchInterval: RTA_POLL_MS,
    gcTime: 2 * 60 * 1000,
  })
}

export function rtaAlertQuery(rtaId: string, alertId: string) {
  return queryOptions({
    queryKey: ['rta', rtaId, 'alert', alertId],
    queryFn: async () => {
      const res = await api.api.rtas[':id'].alerts[':alertId'].$get({
        param: { id: rtaId, alertId },
      })
      if (!res.ok) return fail(res, 'Failed to load the alert')
      return res.json()
    },
    // Full text rides this response; don't keep a day of articles in the heap.
    gcTime: 60 * 1000,
  })
}

export function rtaEventsQuery(rtaId: string, alertId?: string) {
  return queryOptions({
    queryKey: ['rta', rtaId, 'events', alertId ?? null],
    queryFn: async () => {
      const res = await api.api.rtas[':id'].events.$get({
        param: { id: rtaId },
        query: alertId ? { alertId } : {},
      })
      if (!res.ok) return fail(res, 'Failed to load the activity')
      return res.json()
    },
  })
}

function invalidateRta(queryClient: QueryClient, rtaId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['rta', rtaId] }),
    queryClient.invalidateQueries({ queryKey: ['rtas'] }),
    queryClient.invalidateQueries({ queryKey: ['rta-counts'] }),
  ])
}

export function useCreateRta(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { subaccountId: string; name: string }) => {
      const res = await api.api.rtas.$post({ json: input })
      if (!res.ok) return fail(res, 'Failed to create the RTA')
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rtas'] }),
  })
}

export function useUpdateRta(rtaId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (patch: {
      name?: string
      status?: RtaStatus
      settings?: RtaSettings
      coverage?: CoverageRuleInput[]
    }) => {
      const res = await api.api.rtas[':id'].$patch({ param: { id: rtaId }, json: patch })
      if (!res.ok) return fail(res, 'Failed to save the RTA')
      return res.json()
    },
    onSuccess: (rta) => {
      queryClient.setQueryData(rtaQuery(rtaId).queryKey, rta)
      return invalidateRta(queryClient, rtaId)
    },
  })
}

/** Delete a paused RTA and its alerts (CLI-901). The server refuses one that is still active. */
export function useDeleteRta(rtaId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async () => {
      const res = await api.api.rtas[':id'].$delete({ param: { id: rtaId } })
      if (!res.ok) return fail(res, 'Failed to delete the RTA')
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['rta', rtaId] })
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['rtas'] }),
        queryClient.invalidateQueries({ queryKey: ['rta-counts'] }),
      ])
    },
  })
}

export function useSetRtaTopics(rtaId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (topics: RtaTopicInput[]) => {
      const res = await api.api.rtas[':id'].topics.$put({ param: { id: rtaId }, json: { topics } })
      if (!res.ok) return fail(res, 'Failed to save topics')
      return res.json()
    },
    onSuccess: (rta) => {
      queryClient.setQueryData(rtaQuery(rtaId).queryKey, rta)
      // Listening on a topic records that RTAs use it (CLI-744) — the badges read it.
      return Promise.all([
        invalidateRta(queryClient, rtaId),
        queryClient.invalidateQueries({ queryKey: ['topics'] }),
      ])
    },
  })
}

type BulkAction = 'delete' | 'restore' | 'send' | 'dismiss' | 'reopen'

/** Delete / restore / send a selection. Resolves with what was done and what was refused. */
export function useRtaBulkAction(rtaId: string, action: BulkAction, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (alertIds: string[]) => {
      const routes = api.api.rtas[':id'].alerts
      const args = { param: { id: rtaId }, json: { alertIds } }
      const res = await routes[action].$post(args)
      if (!res.ok) return fail(res, `Failed to ${action}`)
      return res.json()
    },
    onSettled: () => invalidateRta(queryClient, rtaId),
  })
}

export function useEditRtaAlert(rtaId: string, alertId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (patch: RtaAlertOverridesPatch) => {
      const res = await api.api.rtas[':id'].alerts[':alertId'].$patch({
        param: { id: rtaId, alertId },
        json: patch,
      })
      if (!res.ok) return fail(res, 'Failed to save the alert')
      return res.json()
    },
    onSuccess: () => invalidateRta(queryClient, rtaId),
  })
}

/**
 * An image for an alert's full text (CLI-769). Nothing to invalidate: the response's path goes
 * into the editor, and the alert's save is what records it.
 */
export function useUploadRtaImage(rtaId: string) {
  // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation
  return useMutation({
    mutationFn: async (input: {
      fileName: string
      contentType: TemplateAssetContentType
      dataBase64: string
    }) => {
      const res = await api.api.rtas[':id'].images.$post({ param: { id: rtaId }, json: input })
      if (!res.ok) return fail(res, 'Failed to upload the image')
      return res.json()
    },
  })
}

export function useAddRtaAlert(rtaId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: AddRtaAlert) => {
      const res = await api.api.rtas[':id'].alerts.$post({ param: { id: rtaId }, json: input })
      if (!res.ok) return fail(res, 'Failed to add the alert')
      return res.json()
    },
    onSuccess: () => invalidateRta(queryClient, rtaId),
  })
}

export async function searchRtaHitsByUrl(rtaId: string, url: string) {
  const res = await api.api.rtas[':id'].search.$get({ param: { id: rtaId }, query: { url } })
  if (!res.ok) return fail(res, 'Search failed')
  return res.json()
}

export function useRunRtas(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async () => {
      const res = await api.api.rtas.run.$post()
      if (!res.ok) return fail(res, 'Check failed')
      return res.json()
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['rta'] }),
        queryClient.invalidateQueries({ queryKey: ['rtas'] }),
        queryClient.invalidateQueries({ queryKey: ['rta-counts'] }),
      ]),
  })
}

/** Clone a topic as "<topic> – RTA" and attach the copy in the original's place. */
export function useCloneRtaTopic(rtaId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { topicId: string; name?: string }) => {
      const res = await api.api.rtas[':id']['clone-topic'].$post({
        param: { id: rtaId },
        json: input,
      })
      if (!res.ok) return fail(res, 'Failed to clone the topic')
      return res.json()
    },
    onSuccess: ({ rta }) => {
      queryClient.setQueryData(rtaQuery(rtaId).queryKey, rta)
      // A new topic exists in the client's list now.
      return Promise.all([
        invalidateRta(queryClient, rtaId),
        queryClient.invalidateQueries({ queryKey: ['topics', rta.subaccountId] }),
      ])
    },
  })
}

/** What this environment can deliver (email live/off, Slack live/stub, the kill switch). */
export const rtaDeliveryQuery = queryOptions({
  queryKey: ['rta-delivery'],
  queryFn: async () => {
    const res = await api.api.rtas.delivery.$get()
    if (!res.ok) return fail(res, 'Failed to load delivery status')
    return res.json()
  },
  staleTime: 60_000,
})

/** Send alerts on one channel — one email or Slack post each. */
export function useSendRtaAlerts(rtaId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: {
      alertIds: string[]
      channel: 'email' | 'slack'
      recipientGroupId?: string
    }) => {
      const res = await api.api.rtas[':id'].alerts.send.$post({ param: { id: rtaId }, json: input })
      if (!res.ok) return fail(res, 'Failed to send')
      return res.json()
    },
    onSettled: () => invalidateRta(queryClient, rtaId),
  })
}

/** Several alerts in ONE email — legacy's "Send bundle". */
export function useSendRtaBundle(rtaId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: {
      alertIds: string[]
      recipientGroupId?: string
      subject?: string
    }) => {
      const res = await api.api.rtas[':id'].alerts['send-bundle'].$post({
        param: { id: rtaId },
        json: input,
      })
      if (!res.ok) return fail(res, 'Failed to send the bundle')
      return res.json()
    },
    onSettled: () => invalidateRta(queryClient, rtaId),
  })
}
